import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  BookmarkSimple,
  CheckCircle,
} from '@phosphor-icons/react';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Separator } from '../components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModulePage = ({ courseId }) => {
  const { slug } = useParams();
  const { userId } = useUser();
  const navigate = useNavigate();
  const [module, setModule] = useState(null);
  const [allModules, setAllModules] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);

  // Get module order from allModules
  const moduleOrder = useMemo(() => {
    return allModules.map(m => m.slug);
  }, [allModules]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [moduleRes, modulesRes, bookmarksRes, progressRes] = await Promise.all([
          axios.get(`${API}/courses/${courseId}/modules/${slug}`),
          axios.get(`${API}/courses/${courseId}/modules`),
          axios.get(`${API}/bookmarks/${userId}`),
          axios.get(`${API}/progress/${userId}`)
        ]);
        setModule(moduleRes.data);
        setAllModules(modulesRes.data);
        setBookmarks(bookmarksRes.data.filter(b => b.module_slug === slug && (b.course_id === courseId || !b.course_id)));
        const moduleProgress = progressRes.data.find(p => p.module_slug === slug && (p.course_id === courseId || !p.course_id));
        setProgress(moduleProgress);
        setIsCompleted(moduleProgress?.completed || false);
      } catch (error) {
        toast.error('Failed to load module');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, userId, courseId]);

  const handleBookmark = async (section) => {
    const existing = bookmarks.find(b => b.section === section);
    if (existing) {
      try {
        await axios.delete(`${API}/bookmarks/${existing.id}`);
        setBookmarks(bookmarks.filter(b => b.id !== existing.id));
        toast.success('Bookmark removed');
      } catch (error) {
        toast.error('Failed to remove bookmark');
      }
    } else {
      try {
        const response = await axios.post(`${API}/bookmarks`, {
          user_id: userId,
          module_slug: slug,
          course_id: courseId,
          section
        });
        setBookmarks([...bookmarks, { id: response.data.id, section, module_slug: slug, course_id: courseId }]);
        toast.success('Bookmarked!');
      } catch (error) {
        toast.error('Failed to bookmark');
      }
    }
  };

  const handleMarkComplete = async () => {
    try {
      await axios.post(`${API}/progress`, {
        user_id: userId,
        module_slug: slug,
        course_id: courseId,
        completed: true,
        progress_percentage: 100
      });
      setIsCompleted(true);
      toast.success('Module completed! 🎉');
    } catch (error) {
      toast.error('Failed to mark as complete');
    }
  };

  const goToNextModule = () => {
    const currentIndex = moduleOrder.indexOf(slug);
    if (currentIndex < moduleOrder.length - 1) {
      navigate(`/${courseId}/${moduleOrder[currentIndex + 1]}`);
    } else {
      navigate(`/certificate/${courseId}`);
    }
  };

  const goToPreviousModule = () => {
    const currentIndex = moduleOrder.indexOf(slug);
    if (currentIndex > 0) {
      navigate(`/${courseId}/${moduleOrder[currentIndex - 1]}`);
    } else {
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="font-mono text-[hsl(var(--text-muted))]">Loading module...</p>
      </div>
    );
  }

  if (!module) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[hsl(var(--destructive))]">Module not found</p>
      </div>
    );
  }

  const isBookmarked = (section) => bookmarks.some(b => b.section === section);
  const currentIndex = moduleOrder.indexOf(slug);
  const totalModules = moduleOrder.length;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero - Dark */}
      <section className="py-16 md:py-24 px-6 md:px-12 border-b border-[#262626] bg-gradient-to-b from-[#0A0A0A] to-[#141414]">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/courses"
              data-testid="back-home-link"
              className="inline-flex items-center text-sm font-mono uppercase tracking-[0.2em] text-[#C4A661] hover:text-[#F7F7F5] transition-colors mb-8"
            >
              <ArrowLeft size={16} className="mr-2" weight="bold" />
              Back to Courses
            </Link>
            <div className="flex items-center justify-between mb-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#A3A3A3]">
                {module.title.toLowerCase().includes('bonus') ? 'Bonus Module' : `Module ${module.order + 1} of ${totalModules}`}
              </span>
              {isCompleted && (
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle size={20} weight="fill" />
                  <span className="font-mono text-xs uppercase tracking-[0.2em]">Completed</span>
                </div>
              )}
            </div>
            <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-[0.95] mb-6 text-[#F7F7F5]">
              {module.title}
            </h1>
            <p className="text-xl md:text-2xl text-[#A3A3A3] leading-relaxed italic">
              {module.content.identity_statement}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content - Light */}
      <section className="py-16 md:py-24 px-6 md:px-12 bg-[#FDFDFD]">
        <div className="container mx-auto max-w-3xl space-y-16">
          {/* Core Teaching */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            data-testid="core-teaching-section"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-black text-[#0A0A0A]">Core Teaching</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleBookmark('core-teaching')}
                data-testid="bookmark-core-teaching"
                className="rounded-none hover:text-[#C4A661]"
                aria-label="Bookmark Core Teaching"
              >
                <BookmarkSimple
                  size={24}
                  weight={isBookmarked('core-teaching') ? 'fill' : 'regular'}
                  className={isBookmarked('core-teaching') ? 'text-[#C4A661]' : 'text-[#525252]'}
                />
              </Button>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed whitespace-pre-wrap text-[#525252]">
                {module.content.core_teaching}
              </p>
            </div>
          </motion.div>

          <Separator className="bg-[#E5E5E5]" />

          {/* Key Principles */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            data-testid="key-principles-section"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-black text-[#0A0A0A]">Key Principles</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleBookmark('key-principles')}
                data-testid="bookmark-key-principles"
                className="rounded-none hover:text-[#C4A661]"
                aria-label="Bookmark Key Principles"
              >
                <BookmarkSimple
                  size={24}
                  weight={isBookmarked('key-principles') ? 'fill' : 'regular'}
                  className={isBookmarked('key-principles') ? 'text-[#C4A661]' : 'text-[#525252]'}
                />
              </Button>
            </div>
            <div className="space-y-8">
              {module.content.key_principles.map((principle, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="border-l-4 border-[#C4A661] pl-6 py-4 bg-white rounded-r-lg shadow-sm"
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-bold pr-4 text-[#0A0A0A]">{principle.title}</h3>
                    <span className="font-mono text-sm text-[#C4A661] flex-shrink-0 bg-[#C4A661]/10 px-2 py-1 rounded">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-base leading-relaxed whitespace-pre-wrap text-[#525252]">
                      {principle.teaching}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <Separator className="bg-[#E5E5E5]" />

          {/* Food for Thought */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            data-testid="food-for-thought-section"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-black text-[#0A0A0A]">Food for Thought</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleBookmark('food-for-thought')}
                data-testid="bookmark-food-for-thought"
                className="rounded-none hover:text-[#C4A661]"
                aria-label="Bookmark Food for Thought"
              >
                <BookmarkSimple
                  size={24}
                  weight={isBookmarked('food-for-thought') ? 'fill' : 'regular'}
                  className={isBookmarked('food-for-thought') ? 'text-[#C4A661]' : 'text-[#525252]'}
                />
              </Button>
            </div>
            <div className="space-y-4">
              {module.content.food_for_thought.map((question, index) => (
                <Card
                  key={index}
                  className="p-6 bg-white rounded-lg border border-[#E5E5E5] shadow-sm"
                >
                  <p className="text-base leading-relaxed italic text-[#525252]">{question}</p>
                </Card>
              ))}
            </div>
          </motion.div>

          <Separator className="bg-[hsl(var(--border))]" />

          {/* Real Life Application */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            data-testid="application-section"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-black text-[#0A0A0A]">Real Life Application</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleBookmark('application')}
                data-testid="bookmark-application"
                className="rounded-none hover:text-[#C4A661]"
                aria-label="Bookmark Application"
              >
                <BookmarkSimple
                  size={24}
                  weight={isBookmarked('application') ? 'fill' : 'regular'}
                  className={isBookmarked('application') ? 'text-[#C4A661]' : 'text-[#525252]'}
                />
              </Button>
            </div>
            <Card className="p-8 bg-[#C4A661]/10 rounded-lg border border-[#C4A661]/30">
              <p className="text-lg leading-relaxed whitespace-pre-wrap text-[#0A0A0A]">
                {module.content.real_life_application}
              </p>
            </Card>
          </motion.div>

          <Separator className="bg-[#E5E5E5]" />

          {/* Closing Charge */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            data-testid="closing-charge-section"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-3xl font-black text-[#0A0A0A]">Closing Charge</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleBookmark('closing-charge')}
                data-testid="bookmark-closing-charge"
                className="rounded-none hover:text-[#C4A661]"
                aria-label="Bookmark Closing Charge"
              >
                <BookmarkSimple
                  size={24}
                  weight={isBookmarked('closing-charge') ? 'fill' : 'regular'}
                  className={isBookmarked('closing-charge') ? 'text-[#C4A661]' : 'text-[#525252]'}
                />
              </Button>
            </div>
            <div className="bg-[#0A0A0A] text-[#F7F7F5] p-8 md:p-12 border-4 border-[#C4A661] rounded-lg">
              <p className="text-xl md:text-2xl leading-relaxed font-medium">
                {module.content.closing_charge}
              </p>
            </div>
          </motion.div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 pt-8">
            {!isCompleted && (
              <Button
                onClick={handleMarkComplete}
                data-testid="mark-complete-btn"
                size="lg"
                className="flex-1 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 transition-all active:scale-95"
              >
                <CheckCircle size={24} weight="bold" className="mr-2" />
                Mark as Complete
              </Button>
            )}
            <Button
              onClick={goToNextModule}
              data-testid="next-module-btn"
              size="lg"
              className="flex-1 rounded-lg bg-[#0A0A0A] text-white hover:bg-[#C4A661] hover:text-[#0A0A0A] transition-all active:scale-95"
            >
              {currentIndex === moduleOrder.length - 1 ? 'Get Certificate' : 'Next Module'}
              <ArrowRight size={24} weight="bold" className="ml-2" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ModulePage;
